import React from 'react';
import { Helmet } from "react-helmet-async";
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight } from 'lucide-react';

const blogPosts = [
  {
    id: 1,
    title: "The Future of Digital Transformation in 2026",
    excerpt: "Explore how AI and cloud computing are reshaping enterprise ecosystems and driving efficiency.",
    author: "Admin",
    date: "March 1, 2026",
    image: "/assets/v/item4.png",
    category: "Technology"
  },
  {
    id: 2,
    title: "Why Performance Engineering is Critical for E-commerce",
    excerpt: "Slow websites kill conversions. Learn how to optimize your platform for peak traffic periods.",
    author: "Tech Team",
    date: "Feb 25, 2026",
    image: "/assets/v/item5.png",
    category: "Engineering"
  },
  {
    id: 3,
    title: "Securing Enterprise Applications: Best Practices",
    excerpt: "Data security is non-negotiable. Discover the top security protocols for modern software development.",
    author: "Security Expert",
    date: "Feb 15, 2026",
    image: "/assets/v/item6.png",
    category: "Security"
  }
];

const BlogPage = () => {
  return (
    <div className="min-h-screen bg-white">
   <Helmet>
    <title>
        Insights & Blogs | Technology, Digital Transformation & Innovation | Mobikode
    </title>

    <meta
        name="description"
        content="Explore Mobikode's latest insights and blogs on digital transformation, AI, cloud computing, software development, performance engineering, enterprise applications, cybersecurity, and emerging technology trends."
    />

    <meta
        name="keywords"
        content="Mobikode Blog, Technology Blog, Technology Insights, Digital Transformation Blog, Digital Transformation Insights, Software Development Blog, AI Technology Trends, Cloud Computing Insights, Performance Engineering, Enterprise Application Security, Cybersecurity Best Practices, Technology Consulting Insights, Software Engineering Trends, IT Industry Insights"
    />

    <link
        rel="canonical"
        href="https://www.mobikode.com/blog"
    />

    <meta
        property="og:title"
        content="Insights & Blogs | Technology, Digital Transformation & Innovation | Mobikode"
    />

    <meta
        property="og:description"
        content="Stay informed with Mobikode's insights on digital transformation, AI, cloud computing, software engineering, performance optimization, enterprise security, and the latest technology trends."
    />

    <meta
        property="og:url"
        content="https://www.mobikode.com/blog"
    />

    <meta
        property="og:type"
        content="website"
    />

    <meta
        property="og:image"
        content="https://www.mobikode.com/assets/what/item3.png"
    />

    <meta
        name="twitter:card"
        content="summary_large_image"
    />

    <meta
        name="twitter:title"
        content="Insights & Blogs | Technology, Digital Transformation & Innovation | Mobikode"
    />

    <meta
        name="twitter:description"
        content="Discover practical insights and the latest trends in AI, cloud, digital transformation, software development, performance engineering, and enterprise technology."
    />

    <meta
        name="twitter:image"
        content="https://www.mobikode.com/assets/what/item3.png"
    />
</Helmet>
      <section className="relative w-full h-[600px] lg:h-[700px] flex items-center bg-[#000814] overflow-hidden">

        {/* 1. Background Image (Absolute) */}
        <div className="absolute inset-0 z-0">
          <img
            src="/assets/what/item3.png"
            alt="Abstract 3D Background"
            className="w-full h-full object-cover"
          />
          <div style={{ background: "linear-gradient(86.84deg, #000000 20%, rgba(0, 0, 0, 0) 79%)" }} className="absolute inset-0 "></div>
        </div>
        <div className="relative z-10 w-full max-w-[1440px] mx-auto px-[20px] lg:px-[80px] flex flex-col items-start justify-center h-full">

          <div className="mt-16 lg:mt-0">
            <h1 className="text-white font-[600] text-[40px] md:text-[60px] font-sora leading-[1.1] mb-[22px] drop-shadow-xl tracking-wide text-left">
              Insights & Blogs
            </h1>

            <p className="text-[#FFFFFF] text-[14px] md:text-[16px] leading-[24px] font-graphik font-[500] tracking-wider text-left">
              Stay updated with the latest trends in software development, digital transformation, and technology consulting.
            </p>

          </div>

        </div>

      </section>

      {/* --- Blog Grid --- */}
      <section className="py-16 lg:py-24">
        <div className="max-w-[1200px] mx-auto px-5">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {blogPosts.map((post) => (
              <div key={post.id} className="group border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300">
                <div className="h-[220px] bg-gray-200 overflow-hidden">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <span className="text-[#0E2A62] text-[14px] lg:text-[16px] font-[600] font-sora">{post.category}</span>
                  <h3 className="lg:text-[20px] text-[16px] font-[600] text-[#0E2A62] font-sora mt-3 mb-4 leading-tight group-hover:text-[#0E2A62] transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-500 text-[14px] lg:text-[16px] font-[500] font-graphik mb-6 line-clamp-2">{post.excerpt}</p>

                  <div className="flex items-center justify-between pt-5 border-t border-gray-50">
                    <div className="flex items-center text-[14px] lg:text-[16px] font-[500] font-graphik gap-2 text-gray-400">
                      <Calendar size={14} /> <span>{post.date}</span>
                    </div>
                    <Link to={`/blog/${post.id}`} className="text-[#0E2A62] text-[14px] lg:text-[16px] font-[500] font-graphik flex items-center gap-1">
                      Read More <ArrowRight size={16} />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;